import { RouteObject } from 'react-router'
import { ProtectedRoutes } from '@/common/layouts/ProtectedRoutes.tsx'
import PmTermSheet from '@/pages/document-autofill/term-sheets/pm-term-sheet.tsx'
import { PageLayout } from '@/common/layouts/PageLayout.tsx'
import { Button, Flex, Popover, Select, Typography } from 'antd'
import { MacroTermSheet } from '@/pages/document-autofill/term-sheets/macro-term-sheet.tsx'
import React from 'react'
import { SelectOption } from '@/common/types/components.def.ts'
import {
  TermSheetCandidateType,
  TermSheetCandidateTypeOptions,
  TermSheetConfigProvider,
} from '@/pages/document-autofill/hooks/useTermSheetConfig.tsx'
import { useStateWithSearchParams } from '@/common/hooks/useStateWithSearchParams.ts'
import { DetailItem } from '@/common/components/DetailItem.tsx'
import { Country, CountryOptions } from '@/common/types/work-info.def.ts'
import { BooleanCheckbox } from '@/common/components/AntdForm/AntdFormBoolean.tsx'
import { QuestionCircleOutlined } from '@ant-design/icons'

const termSheetOptions: SelectOption[] = [
  {
    label: 'Term Sheet',
    value: 'term-sheet',
  },
  {
    label: 'Macro Term Sheet',
    value: 'macro-term-sheet',
  },
]

const currencyOptionsPopoverContent = (
  <div className="max-w-[720px] space-y-3">
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr>
          <th className="border border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold">Candidate Type</th>
          <th className="border border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold">Base Salary</th>
          <th className="border border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold">Bonus</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className="border border-gray-200 px-3 py-2 font-medium">PM</td>
          <td className="border border-gray-200 px-3 py-2">Local currency</td>
          <td className="border border-gray-200 px-3 py-2">USD</td>
        </tr>
        <tr>
          <td className="border border-gray-200 px-3 py-2 font-medium">Sub-PM / APM</td>
          <td className="border border-gray-200 px-3 py-2">Local currency</td>
          <td className="border border-gray-200 px-3 py-2">Local currency*</td>
        </tr>
        <tr>
          <td className="border border-gray-200 px-3 py-2 font-medium">Analyst</td>
          <td className="border border-gray-200 px-3 py-2">Local currency</td>
          <td className="border border-gray-200 px-3 py-2">Local currency</td>
        </tr>
      </tbody>
    </table>
    <div className="space-y-1 text-xs text-gray-600">
      <Typography.Text strong className="text-xs">*Exception (Sub-PM / APM):</Typography.Text>
      <div>If the Hiring PM is a Principal, the Hiring Principal can choose USD or Local Currency for the contract.</div>
      <div>
        If USD is specified in the contract, they may later choose to pay in either USD or Local Currency closer to the
        payment date.
      </div>
    </div>
  </div>
)

export const TermSheets: React.FC = () => {
  const [activeKey, setActiveKey] = useStateWithSearchParams('type', 'term-sheet')
  const [candidateType, setCandidateType] = useStateWithSearchParams<string>('candidateType', TermSheetCandidateType.PM)
  const [region, setRegion] = useStateWithSearchParams<string>('region', Country.HongKong)
  const [isHiringManagerPrincipal, setIsHiringManagerPrincipal] = useStateWithSearchParams<string>(
    'isHiringManagerPrincipal',
    'false',
  )
  const booleanIsHiringManagerPrincipal = isHiringManagerPrincipal === 'true'

  return (
    <PageLayout>
      <Typography.Title level={2}>Please input the following info to proceed</Typography.Title>
      <Flex gap="small">
        <DetailItem
          label="Term Sheet Type (PM or Marco PM)"
          value={<Select options={termSheetOptions} value={activeKey} onChange={setActiveKey} />}
        />
        <DetailItem
          label="Candidate Type"
          value={<Select options={TermSheetCandidateTypeOptions} value={candidateType} onChange={setCandidateType} />}
        />
        <DetailItem
          label="Region"
          value={<Select options={CountryOptions} value={region} onChange={setRegion} />}
        />
        <DetailItem
          label="Is Hiring Manager Principal?"
          value={<BooleanCheckbox
            value={booleanIsHiringManagerPrincipal}
            onChange={x => setIsHiringManagerPrincipal((!!x).toString())}
          />}
        />
        <Popover content={currencyOptionsPopoverContent}>
          <Button icon={<QuestionCircleOutlined />} type="link" size="large">Currency Options</Button>
        </Popover>
      </Flex>
      {activeKey && candidateType && region ? (
        <TermSheetConfigProvider
          region={region}
          candidateType={candidateType}
          isHiringManagerPrincipal={booleanIsHiringManagerPrincipal}
        >
          {
            activeKey === 'term-sheet' ? (
              <PmTermSheet />
            ) : (
              <MacroTermSheet />
            )
          }
        </TermSheetConfigProvider>
      ) : null}
    </PageLayout>
  )
}


export const termSheetsRoutes: RouteObject = {
  path: 'document-autofill/term-sheets',
  element: (
    <ProtectedRoutes requiredPermissions={[{ moduleName: 'document-autofill', functionName: 'create-term-sheet' }]} />
  ),
  children: [
    {
      index: true, element: (
        <TermSheets />
      ),
    },
  ],
}